import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { withAuthenticator, useAuthenticator, type WithAuthenticatorProps } from '@aws-amplify/ui-react';
import { Dashboard } from './routes/Dashboard';
import { Session } from './routes/Session';
import { Photos } from './routes/Photos';
import { Book } from './routes/Book';
import { Profile } from './routes/Profile';
import { Stories } from './routes/Stories';
import { Admin } from './routes/Admin';
import { WhatsNew } from './routes/WhatsNew';
import { Orders } from './routes/Orders';
import { fetchProfile } from './lib/profile-api';
import { fetchStoryTopics } from './lib/story-topics-api';
import { FeedbackButton } from './components/FeedbackModal/FeedbackButton';

function App({ signOut, user }: WithAuthenticatorProps) {
  if (!user) {
    // Defensive — withAuthenticator should never render App without a user
    return null;
  }

  const [profileChecked, setProfileChecked] = useState(false);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [needsStoryPlan, setNeedsStoryPlan] = useState(false);

  useEffect(() => {
    // Only run once per session mount — check if profile exists
    fetchProfile()
      .then((profile) => {
        if (profile === null) {
          setNeedsProfile(true);
          setProfileChecked(true);
        } else {
          setNeedsProfile(false);
          // Profile exists — check if story plan has been set up
          return fetchStoryTopics()
            .then((topics) => {
              setNeedsStoryPlan(topics.length === 0);
            })
            .catch(() => {
              // On error, don't redirect — treat as "plan exists"
            })
            .finally(() => {
              setProfileChecked(true);
            });
        }
      })
      .catch(() => {
        // On error, don't block the app — treat as "profile exists"
        setProfileChecked(true);
      });
  }, []);

  // Show nothing until profile check resolves (avoids flash of Dashboard)
  if (!profileChecked) return null;

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            needsProfile ? (
              <Navigate to="/profile" replace />
            ) : needsStoryPlan ? (
              <Navigate to="/stories" replace />
            ) : (
              <Dashboard signOut={signOut} />
            )
          }
        />
        <Route path="/session/new" element={<Session />} />
        <Route path="/session/:sessionId" element={<Session />} />
        <Route path="/photos" element={<Photos />} />
        <Route path="/book" element={<Book />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/stories" element={<Stories />} />
        <Route path="/plan" element={<Navigate to="/stories" replace />} />
        <Route path="/curriculum" element={<Navigate to="/stories" replace />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/whats-new" element={<WhatsNew />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <FeedbackButton />
    </>
  );
}

function BetaAccessFooter() {
  const { route } = useAuthenticator(ctx => [ctx.route]);
  if (route !== 'signIn') return null;
  return (
    <div style={{ textAlign: 'center', paddingBottom: '20px' }}>
      <a
        href="https://mylifehistory.xyz/#beta-signup"
        style={{ color: '#D49A6A', fontSize: '14px', textDecoration: 'none' }}
      >
        Don't have an account? Request beta access →
      </a>
    </div>
  );
}

function parseOAuthError(raw: string): string {
  // Strip Cognito's internal prefix: "PreSignUp failed with error <message>."
  const match = raw.match(/PreSignUp failed with error (.+?)\.?\s*$/);
  const msg = match ? match[1].replace(/\.\s*$/, '') : raw;
  if (msg.toLowerCase().includes('not on the approved beta list')) {
    return "Your email isn't on the beta access list. Request access at mylifehistory.xyz.";
  }
  return msg;
}

const AuthenticatedApp = withAuthenticator(App, {
  loginMechanisms: ['email'],
  signUpAttributes: ['email'],
  socialProviders: ['google', 'apple'],
  hideSignUp: true,
  components: {
    SignIn: {
      Footer: BetaAccessFooter,
    },
  },
});

function AppWithOAuthErrorHandling() {
  const [oauthError, setOauthError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const desc = params.get('error_description');
    if (error && desc) {
      setOauthError(parseOAuthError(decodeURIComponent(desc)));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <>
      {oauthError && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#7f1d1d', color: '#fff',
          padding: '14px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: '14px', lineHeight: '1.4',
        }}>
          <span>{oauthError}</span>
          <button
            onClick={() => setOauthError(null)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '18px', marginLeft: '16px', lineHeight: 1 }}
            aria-label="Dismiss"
          >×</button>
        </div>
      )}
      <AuthenticatedApp />
    </>
  );
}

export default AppWithOAuthErrorHandling;
